import React, { useEffect } from 'react';
import { connect, useSelector } from 'react-redux';
import {
  reduxForm,
  Field,
  Fields,
  FieldArray,
  formValueSelector,
  SubmissionError,
} from 'redux-form/immutable';
import type { FormErrors } from 'redux-form';
import { Radio, RadioGroup, FormControlLabel, Tooltip } from '@material-ui/core/';
import Button from '@material-ui/core/Button';
import Grid from '@material-ui/core/Grid';
import FormControl from '@material-ui/core/FormControl';
import InputLabel from '@material-ui/core/InputLabel';
import FormLabel from '@material-ui/core/FormLabel';
import type { InjectedFormProps, FormSubmitHandler, SubmitHandler } from 'redux-form/lib/reduxForm';
import { type Iterable as ImmutableIterable, type Map as ImmutableMap, List } from 'immutable';
import { Alert } from '@mui/material';

import { RenderHtml } from 'components/RenderHtml';
import FormRadioButton from 'retired/FormRadioButton';
import FormRoles from 'retired/FormRoles';
import FormLocationField from 'retired/FormLocationField';
import FormTextFieldWithCheckbox from 'retired/FormTextFieldWithCheckbox';
import FormDescriptionRteField from 'retired/FormDescriptionRteField';
import FormTextField from 'retired/FormTextField';
import FormCheckbox from 'retired/FormCheckbox';
import FormPublishingStatus from 'retired/FormPublishingStatus';
import FormPartnerSelector from 'retired/FormPartnerSelector';
import FormMilestones from 'retired/FormMilestones';
import PhoneNumber from 'retired/PhoneNumberField';
import FormValueSelector from 'retired/FormValueSelector';
import FormPictureSelector from 'retired/FormPictureSelector';
import FormFormsSelector from 'retired/FormFormsSelector';
import FormEditor from 'retired/FormEditor';
import StepperComponent, { Stepper } from 'retired/Stepper';
import FormLocationSelector from 'retired/FormLocationSelector';
import { red } from 'utils/color-styles';
import { PureRender } from 'utils/component-helpers';
import { fieldArrayHasErrors } from 'utils/reduxForm';
import addressToLocation from 'utils/addressToLocation';
import { scrollToError } from 'utils/scroll.helper';
import { Privileges } from 'utils/privileges';
import { useQueryParams } from 'utils/useQueryParams';
import {
  SelectCommunities,
  useCommunitiesList,
  SelectOrganizingCommunity,
} from 'containers/DeedFormPage/CommunitySelect';
import { Features } from 'utils/features';
import type User from 'entities/user/model';
import type Campaign from 'entities/campaign/model';
import type Cause from 'entities/cause/model';
import FormSelectedAndOptedInCompanies from 'retired/FormSelectedAndOptedInCompanies';
import { OrganizationType } from 'generated/graphql';
import { Partner } from 'entities/partners/api';
import Organization from 'entities/organization/model';
import Deed from 'entities/deed/model';

import { FORM_IDENTIFIER } from '../constants';
import { selectPartnersLoading } from '../selectors';
import { shareTooltip } from '../copy';
import type { DeedFormStatus } from '../formUtils';
import {
  isAddress,
  isEmail,
  isNumber,
  isPhone,
  isURL,
  maxLength5,
  maxLength5000,
  notEmpty,
  required,
  requiredRadioButton,
} from '../validators';
import { ProvisionalNonprofitDataBanner } from '../ProvisionalNonprofitDataBanner';

type DeedProjectFormProps = {
  deed: Deed;
  companyOrPublic: string;
  isFeatured: boolean;
  id: string;
  name: string;
  mainPicture: string;
  organization: Organization;
  sharedWithCompanies: List<string>;
  campaigns: ImmutableIterable<string, Campaign>;
  pillars: ImmutableIterable<string, Cause>;
  ERGs: ImmutableIterable<string, Cause>;
  categories: ImmutableIterable<string, Cause>;
  SDGs: ImmutableIterable<string, Cause>;
  roles: unknown;
  skills: unknown;
  forms: unknown;
  pictures: unknown;
  uploadedFile: string;
  actions: unknown;
  attendeeLimit: number;
  partners: Partner[];
  virtual: boolean;
  selectedCategories: unknown;
  selectedPartner: string;
  errors: FormErrors<FormData, string>;
  handleSubmit: SubmitHandler<ImmutableMap<FormData, string>>;
  deedType: string;
  backgroundCheckLink: string;
  externalLink: string;
  location: string;
  partnerName: string;
  partnerEmail: string;
  organizationId: string;
  status: DeedFormStatus;
  user: User;
  locations: unknown;
  allowedLocations: unknown;
  selectedPictures: unknown;
  description: string;
};

const DeedProjectForm = ({
  deed,
  companyOrPublic,
  sharedWithCompanies,
  isFeatured,
  id,
  name,
  mainPicture,
  organization,
  campaigns,
  pillars,
  ERGs,
  roles,
  categories,
  selectedCategories,
  SDGs,
  selectedPartner,
  skills,
  forms,
  uploadedFile,
  pictures,
  actions,
  handleSubmit,
  partners,
  deedType,
  backgroundCheckLink,
  externalLink,
  partnerName,
  partnerEmail,
  attendeeLimit,
  organizationId,
  virtual,
  errors,
  location = '',
  status = 'draft',
  user,
  locations,
  allowedLocations,
  change,
  selectedPictures,
  description,
}: DeedProjectFormProps & InjectedFormProps<FormData>) => {
  const { communities } = useCommunitiesList();

  const queryParams = useQueryParams();
  const duplicate = queryParams.get('duplicate');

  const ensureCompanyOnlyDeeds = organization?.hasSetting('ensureCompanyOnlyDeeds');

  const isCommunityFeatureEnabled = user?.hasFeature(Features.COMMUNITIES);
  const userCanViewCommunities =
    isCommunityFeatureEnabled && user?.hasPrivilege(Privileges.COMMUNITIES_VIEW);

  useEffect(() => {
    if (ensureCompanyOnlyDeeds && (id === 'new' || id === 'past')) {
      change('requireAuthentication', true);
      change('public', false);
    }
  }, [organization]);

  const isCompany = organization?.isCompany();

  useEffect(() => {
    // @NOTE-RT: When an NPO organizer with a corporate partner removes that partner, make them reselect `companyOrPublic`
    if (!isCompany && !selectedPartner && companyOrPublic === 'company') {
      change('companyOrPublic', null);
    }
  }, [companyOrPublic, isCompany, selectedPartner]);

  const handleLocationSelect = (address: string) => addressToLocation(address);

  const partnersLoading = useSelector(selectPartnersLoading);

  const isExternal = id?.indexOf('ext-') === 0 || !!deed?.externalId;
  const enableValidation = status !== 'draft';

  const createOrganizationError =
    isCompany && (partnerName || partnerEmail) && !(partnerName && partnerEmail);
  const unselectOrganizationToCreateNewNonprofitError =
    isCompany && organizationId && (partnerName || partnerEmail);

  const invalid = !!(
    errors.companyOrPublic ||
    errors.name ||
    errors.description ||
    errors.attendeeLimit ||
    fieldArrayHasErrors(errors.roles) ||
    errors.organizerName ||
    errors.organizerEmail ||
    errors.organizerPhone ||
    errors.categories ||
    errors.SDGs ||
    errors.pictures ||
    (!isExternal && createOrganizationError) ||
    unselectOrganizationToCreateNewNonprofitError ||
    (errors.location && !virtual) ||
    fieldArrayHasErrors(errors.milestones) ||
    errors.backgroundCheckLink ||
    errors.forms ||
    fieldArrayHasErrors(errors.formQuestions)
  );

  const submit: FormSubmitHandler<ImmutableMap<FormData, string>> = (
    formData: ImmutableMap<FormData, string>
  ) => {
    // @NOTE-EV: we can't use ReduxForm onSubmitFail prop since combining it with persistentSubmitErrors breaks its behavior
    if (status !== 'draft' && invalid) {
      scrollToError(errors);
      // @NOTE-EV: needs to be thrown manually because FieldArray relies on `submitFail` bool to display error message when the user hasn't interacted with the section.
      throw new SubmissionError({});
    } else {
      const data = formData.toJS() as Partial<FormData>;
      actions.saveProjectAction({
        ...data,
        deedType,
      });
    }
  };

  const deedApprovalRequired =
    partners?.get(selectedPartner)?.deedApprovalRequired &&
    user?.organization?.type === OrganizationType.Nonprofit;

  const initialSharedWithCompanies = List(
    user?.organization?.siblingSubsidiaries?.map((company) => company.id).filter(Boolean) || []
  );

  const nonprofitForms =
    isCompany && deed?.forms?.filter((form) => form.organization === deed?.organization?.id);

  const isAdmin = user?.type === 'Admin';
  const partnerPillars = pillars.filter(
    (pillar) =>
      !!pillar &&
      (isCompany || pillar.organization === selectedPartner || (isAdmin && pillar.individualsOnly))
  );
  const partnerERGs = ERGs.filter(
    (ERG) =>
      !!ERG &&
      !isCommunityFeatureEnabled &&
      (isCompany || ERG.organization === selectedPartner || (isAdmin && ERG.individualsOnly))
  );
  const showCampaignsAndPillarsAndERGs =
    (isAdmin || isCompany || selectedPartner) &&
    (campaigns.size || partnerPillars.size || partnerERGs.size || communities.size);

  const publicToolTip =
    "Selecting 'Public' will enable any individual to view this project, users will still be required to login to apply. Additionally, this project will be publicly available to all Deed users.";
  const companyToolTip = isCompany
    ? "Selecting 'Company Only' will require users to login to verify they are company employees before they can view or apply for this project."
    : "Selecting 'Company Only' will require users to login to verify they are company employees of the Partner Company selected above before they can view or apply for this project.";
  return organization ? (
    <form onSubmit={handleSubmit(submit)}>
      <Stepper orientation="vertical">
        <StepperComponent labelText="Project Name *" componentIndex={0}>
          <Field
            name="name"
            component={FormTextField}
            disabled={isExternal}
            fullWidth
            autoFocus
            inputProps={{ maxLength: 100, autoComplete: 'off' }}
            label="What would you like to name your project? *"
            helperText={
              !isExternal && 'Choose an exciting name for your project! (40 characters max)'
            }
            autoComplete="off"
            validate={required}
          />
        </StepperComponent>

        <StepperComponent labelText="Project Details *" componentIndex={1}>
          {isExternal ? (
            <>
              <p
                style={{
                  color: 'black',
                  opacity: 0.38,
                  textTransform: 'uppercase',
                  fontSize: 12,
                }}
              >
                How would you describe your event? *
              </p>
              <RenderHtml
                source={{ html: description || '' }}
                customStyles={{ color: 'black', opacity: 0.38 }}
              />
            </>
          ) : (
            <>
              {errors.description && (
                <input
                  disabled
                  name="description"
                  style={{ height: 0, width: 0, display: 'block' }}
                />
              )}
              <Field
                name="description"
                component={FormDescriptionRteField}
                fullWidth
                multiline
                inputProps={{ maxLength: 5000, autoComplete: 'off' }}
                label="How would you describe your event? *"
                helperText="Give a description for your event! (5000 characters max)"
                validate={[required, maxLength5000]}
                autoComplete="off"
              />
            </>
          )}
        </StepperComponent>

        {isExternal && (
          <StepperComponent labelText="Volunteer Information" componentIndex={2}>
            <Field
              name="mustKnows"
              disabled
              component={FormTextField}
              inputProps={{ maxLength: 500 }}
              fullWidth
              multiline
              label="What are the must knows?"
            />
            {roles?.size ? null : (
              <>
                <br />
                <br />
                <Field
                  name="attendeeLimit"
                  component={FormTextField}
                  type="number"
                  disabled={!!attendeeLimit}
                  inputProps={{ min: '0', step: 1 }}
                  fullWidth
                  label="How many people will be needed? *"
                  autoComplete="off"
                  validate={[required, isNumber]}
                />
              </>
            )}
          </StepperComponent>
        )}

        {!isExternal && (
          <StepperComponent labelText="Project Roles *" componentIndex={2}>
            {errors.roles && (
              <input disabled name="roles" style={{ height: 0, width: 0, display: 'block' }} />
            )}
            <FieldArray
              name="roles"
              component={FormRoles}
              disabled={isExternal}
              deed={deed}
              skills={skills}
              selectedDeedType={deedType}
              labelText="Which roles are needed for your Project?"
              validate={notEmpty}
            />
          </StepperComponent>
        )}

        <StepperComponent labelText="External application" componentIndex={3}>
          <Field
            disabled={isExternal}
            name="externalLink"
            component={FormTextFieldWithCheckbox}
            defaultValue={externalLink}
            label="Project & application URL"
            helperText={!isExternal && 'Volunteers will be sent to this link'}
            checkboxLabel="Is the application & project hosted outside of Deed?"
            validate={isURL}
          />
        </StepperComponent>

        <StepperComponent labelText="Project Point of Contact *" componentIndex={4}>
          <FormControl style={{ width: '100%', height: 17 }}>
            <InputLabel shrink>
              Please provide a point of contact for this Deed who will serve as a liaison for
              registered volunteers
            </InputLabel>
          </FormControl>
          <Grid container spacing={3}>
            <Grid item md>
              <Field
                id="organizerName"
                name="organizerName"
                component={FormTextField}
                fullWidth
                label="Name *"
                validate={required}
                disabled={isExternal}
              />
            </Grid>
            <Grid item md>
              <Field
                id="organizerEmail"
                name="organizerEmail"
                type="email"
                component={FormTextField}
                fullWidth
                label={`Email${!isExternal ? ' *' : ''}`}
                validate={!isExternal && [required, isEmail]}
                disabled={isExternal}
              />
            </Grid>
            <Grid item md>
              <Field
                id="organizerPhone"
                name="organizerPhone"
                type="tel"
                component={PhoneNumber}
                fullWidth
                label="Phone Number"
                validate={!isExternal && isPhone}
                disabled={isExternal}
                disableInitialCountryCode
              />
            </Grid>
          </Grid>
        </StepperComponent>

        {showCampaignsAndPillarsAndERGs ? (
          <StepperComponent
            labelText={`${!isCompany ? 'Partner p' : 'Campaigns, p'}illars & ${
              userCanViewCommunities ? 'Communities' : 'ERGs'
            }`}
            componentIndex={5}
          >
            <SelectOrganizingCommunity communities={communities} />
            {campaigns.size > 0 && (
              <>
                <FieldArray
                  name="campaigns"
                  component={FormValueSelector}
                  selectMultiple
                  options={campaigns}
                  labelText="Include in campaign"
                />
                <br />
                <br />
              </>
            )}
            {partnerPillars.size > 0 && (
              <FieldArray
                name="pillars"
                component={FormValueSelector}
                options={partnerPillars}
                labelText="Which cause area best describes your deed? (Select a maximum of five.)"
                selectMultiple
                validate={maxLength5}
                maxAmountAllowed={5}
              />
            )}
            {partnerERGs.size > 0 && (
              <>
                <br />
                <br />
                <FieldArray
                  name="ERGs"
                  component={FormValueSelector}
                  selectMultiple
                  options={partnerERGs}
                  labelText="Which ERGs match your Deed? (You may choose more than one.)"
                />
              </>
            )}
            <SelectCommunities communities={communities} />
          </StepperComponent>
        ) : null}

        <StepperComponent
          labelText={`Project Categories${SDGs.size > 0 ? ' & SDGs' : ''} *`}
          componentIndex={showCampaignsAndPillarsAndERGs ? 6 : 5}
        >
          {errors.categories && (
            <input disabled name="categories" style={{ height: 0, width: 0, display: 'block' }} />
          )}
          <FieldArray
            name="categories"
            component={FormValueSelector}
            selectMultiple
            options={categories}
            labelText="Which categories best describe your deed? (Select a maximum of five.) *"
            validate={categories.size > 0 ? [notEmpty, maxLength5] : undefined}
          />
          {SDGs.size > 0 && (
            <>
              <br />
              <br />
              {errors.SDGs && (
                <input disabled name="SDGs" style={{ height: 0, width: 0, display: 'block' }} />
              )}
              <FieldArray
                name="SDGs"
                component={FormValueSelector}
                selectMultiple
                options={SDGs}
                labelText="Which UN SDGs best fit your deed? (Select a maximum of five.) *"
                validate={isExternal ? maxLength5 : [notEmpty, maxLength5]}
              />
            </>
          )}
        </StepperComponent>

        <StepperComponent
          labelText="Project Picture *"
          componentIndex={showCampaignsAndPillarsAndERGs ? 7 : 6}
        >
          {isExternal && mainPicture !== '' ? (
            <img style={{ maxWidth: '500px' }} src={mainPicture} alt={name || ''} />
          ) : (
            <FieldArray
              name="pictures"
              component={FormPictureSelector}
              options={pictures}
              pictures={selectedPictures}
              fileUploadedAction={actions.fileUploadedAction}
              uploadedFile={uploadedFile}
              selectedCategories={selectedCategories}
              labelText="Choose a photo for your Deed (you may choose one of the preselected ones below or upload your own)"
              validate={notEmpty}
            />
          )}

          {errors.pictures && !isExternal && (partnerName || organizationId) && (
            <FormLabel error style={{ fontSize: 12, marginTop: 5 }}>
              Required
            </FormLabel>
          )}
        </StepperComponent>
        <StepperComponent
          labelText={isCompany ? 'Nonprofit information' : 'Partner company information'}
          componentIndex={showCampaignsAndPillarsAndERGs ? 8 : 7}
        >
          <ProvisionalNonprofitDataBanner
            organizationId={organizationId}
            provisionalNonprofitData={deed?.provisionalNonprofitData}
          />
          <Fields
            names={[
              'organization',
              'partner',
              'partnerName',
              'partnerEmail',
              'partnerLocation',
              'partnerEin',
              'partnerPhone',
            ]}
            component={FormPartnerSelector}
            fetchPartnersAction={actions.fetchPartnersAction}
            partnersLoading={partnersLoading}
            partners={partners}
            isCompany={isCompany}
            isFeatured={isFeatured}
            selectedOrganization={deed?.organization}
            user={user}
            initialSelectedSearchTerm={deed?.provisionalNonprofitData?.name || ''}
            sx={{ textTransform: 'uppercase' }}
          />
          {(createOrganizationError || unselectOrganizationToCreateNewNonprofitError) &&
            (virtual ? allowedLocations : location) && (
              <FormLabel error style={{ fontSize: 12, marginTop: 5 }}>
                Required
              </FormLabel>
            )}
        </StepperComponent>

        <StepperComponent
          labelText="Project Site *"
          componentIndex={showCampaignsAndPillarsAndERGs ? 9 : 8}
        >
          <Field
            name="virtual"
            component={FormCheckbox}
            label="This project will be hosted virtually"
            disabled={isExternal}
          />

          {!virtual && (
            <Field
              name="location"
              component={FormLocationField}
              fullWidth
              label="Where is your Event located? *"
              helperText={!isExternal && 'Please enter a full address'}
              autoComplete="off"
              validate={[required, isAddress]}
              afterSelect={handleLocationSelect}
              disabled={isExternal}
              locations={user?.locations}
              sx={{ textTransform: 'uppercase' }}
            />
          )}
          <FormControl style={{ width: '100%', height: 20 }}>
            <InputLabel shrink>
              If this project should be available only to users from a specific location, select the
              location here:
            </InputLabel>
          </FormControl>
          <Field
            name="allowedLocations"
            component={FormLocationSelector}
            props={{ locations, expanded: false }}
            style={{ marginRight: 10, marginBottom: 10 }}
            fullWidth
            disabled={isExternal}
          />
        </StepperComponent>
        {!isExternal && (
          <StepperComponent
            labelText="Milestones"
            componentIndex={showCampaignsAndPillarsAndERGs ? 10 : 9}
          >
            <FieldArray name="milestones" component={FormMilestones} disabled={isExternal} />
          </StepperComponent>
        )}

        <StepperComponent
          labelText="Additional Project Settings"
          componentIndex={showCampaignsAndPillarsAndERGs ? 11 : 10}
        >
          {isCompany && (
            <>
              <Field name="featured" component={FormCheckbox} label="Featured Deed?" />
              <br />
            </>
          )}
          <Field
            name="linkOnly"
            component={FormCheckbox}
            label="Link only: only users with access to the link can view"
          />
          {!isExternal && (
            <>
              <Field
                name="skipApplicationStatusUpdates"
                component={FormCheckbox}
                label="Turn off follow-up email notifications to volunteers about their application status for this project (Approved or Rejected/Project Filled)"
              />
              <Field
                name="backgroundCheckLink"
                component={FormTextFieldWithCheckbox}
                defaultValue={backgroundCheckLink}
                label="Background check link"
                checkboxLabel="Is a volunteer background check required?"
                validate={isURL}
              />
            </>
          )}
        </StepperComponent>

        {!isExternal && (
          <>
            <StepperComponent
              labelText="Volunteer Forms"
              componentIndex={showCampaignsAndPillarsAndERGs ? 12 : 11}
            >
              <FieldArray
                name="forms"
                component={FormFormsSelector}
                options={forms}
                labelText="Choose the forms which would be required for a volunteer to fill in. Go to the Forms tab to create new forms."
              />
              {organization?.parent && (
                <Field
                  name="shareFormResponses"
                  component={FormCheckbox}
                  label="Allow form responses to be visible to other organizations"
                />
              )}
              {nonprofitForms && nonprofitForms.size > 0 && (
                <Alert severity="info" style={{ marginTop: 20 }}>
                  {`This deed is associated with the following form(s) created by the nonprofit ${
                    deed?.organization?.name
                  }:
                ${nonprofitForms?.map((form) => form.name).join(', ')}`}
                </Alert>
              )}
            </StepperComponent>

            <StepperComponent
              labelText="Additional Volunteer Questions"
              componentIndex={showCampaignsAndPillarsAndERGs ? 13 : 12}
            >
              <FieldArray name="formQuestions" component={FormEditor} change={change} />
            </StepperComponent>
          </>
        )}

        {(!ensureCompanyOnlyDeeds || Boolean(user?.organization?.parent)) && (
          <StepperComponent
            labelText="Deed Visibility *"
            componentIndex={showCampaignsAndPillarsAndERGs ? 14 : 13}
          >
            <Field
              name="companyOrPublic"
              component={FormRadioButton}
              validate={requiredRadioButton}
            >
              <RadioGroup row>
                <Tooltip title={companyToolTip}>
                  <FormControlLabel
                    control={<Radio />}
                    value="company"
                    label="Company Only"
                    checked={companyOrPublic === 'company'}
                    disabled={!isCompany && !selectedPartner}
                  />
                </Tooltip>
                <Tooltip title={publicToolTip}>
                  <FormControlLabel
                    control={<Radio />}
                    value="public"
                    label="Public"
                    checked={companyOrPublic === 'public'}
                    disabled={ensureCompanyOnlyDeeds}
                  />
                </Tooltip>
                {user?.organization?.parent && (
                  <Tooltip title={shareTooltip}>
                    <FormControlLabel
                      control={<Radio />}
                      value="shared"
                      label="Share with other Organizations"
                      checked={companyOrPublic === 'shared'}
                      disabled={!isCompany}
                    />
                  </Tooltip>
                )}
              </RadioGroup>
            </Field>
            {isCompany && user?.organization?.parent && companyOrPublic === 'shared' && (
              <Field
                name="sharedWithCompanies"
                component={FormSelectedAndOptedInCompanies}
                optedInCompanies={duplicate ? List() : deed?.optedInCompanies}
                sharedWithCompanies={sharedWithCompanies || initialSharedWithCompanies}
                validate={notEmpty}
              />
            )}
          </StepperComponent>
        )}

        <StepperComponent
          labelText="Review"
          componentIndex={
            /* eslint-disable-next-line no-nested-ternary */
            ensureCompanyOnlyDeeds && showCampaignsAndPillarsAndERGs
              ? 14
              : showCampaignsAndPillarsAndERGs
              ? 15
              : 16
          }
          currentStep={
            /* eslint-disable-next-line no-nested-ternary */
            ensureCompanyOnlyDeeds && showCampaignsAndPillarsAndERGs
              ? 14
              : showCampaignsAndPillarsAndERGs
              ? 15
              : 16
          }
          finalStep
        >
          <div>
            <p>
              {enableValidation && invalid ? (
                <span style={{ color: red }}>
                  Uh oh – there is missing information that is important for volunteers to know!
                  Before you are able to publish this deed, you&apos; need to finish completing the
                  form. For now, your deed can be saved as a draft.
                </span>
              ) : (
                <>
                  Take time to review the information you entered. The deed can be set to pending or
                  published only when all mandatory sections have been filled correctly.
                </>
              )}
            </p>
            <br />
            <br />
            <Field
              name="status"
              component={FormPublishingStatus}
              label="Publishing status:"
              deedApprovalRequired={deedApprovalRequired}
            />
            <br />
            <br />
            <Button type="submit" color="primary" variant="contained">
              {status === 'draft'
                ? 'Save draft'
                : status === 'pending'
                ? 'Submit for review'
                : 'Submit'}
            </Button>
          </div>
        </StepperComponent>
      </Stepper>
    </form>
  ) : null;
};

const selector = formValueSelector(FORM_IDENTIFIER);

type FormData = {
  virtual: boolean;
  backgroundCheckLink: string;
  externalLink: string;
  location: string;
  allowedLocations: unknown;
  partnerName: string;
  partnerEmail: string;
  organizationId: string;
  mainPicture: string;
  name: string;
  attendeeLimit: number; // TODO double check
  id: unknown;
  roles: unknown;
  selectedPictures: unknown;
  companyOrPublic: unknown;
  description: string;
  organizerName: string;
  organizerEmail: string;
  organizerPhone: string;
  categories: ImmutableIterable<string, Cause>;
  SDGs: ImmutableIterable<string, Cause>;
  pictures: unknown;
  milestones: unknown;
  forms: unknown;
  formQuestions: unknown;
  status: DeedFormStatus;
};

export default connect((state) => ({
  virtual: selector(state, 'virtual'),
  backgroundCheckLink: selector(state, 'backgroundCheckLink'),
  externalLink: selector(state, 'externalLink'),
  location: selector(state, 'location'),
  allowedLocations: selector(state, 'allowedLocations'),
  partnerName: selector(state, 'partnerName'),
  partnerEmail: selector(state, 'partnerEmail'),
  organizationId: selector(state, 'organization'),
  mainPicture: selector(state, 'mainPicture'),
  name: selector(state, 'name'),
  attendeeLimit: selector(state, 'attendeeLimit'),
  id: selector(state, 'id'),
  roles: selector(state, 'roles'),
  selectedPictures: selector(state, 'pictures'),
  companyOrPublic: selector(state, 'companyOrPublic'),
  description: selector(state, 'description'),
  sharedWithCompanies: selector(state, 'sharedWithCompanies'),
}))(
  reduxForm<FormData, DeedProjectFormProps>({
    form: FORM_IDENTIFIER,
    enableReinitialize: true,
    keepDirtyOnReinitialize: true,
    persistentSubmitErrors: true,
  })(PureRender(DeedProjectForm))
);
